import type { HexOrientation, ProductGeometry, Rect, ShapeKind } from '../types.ts'
import { insetRect } from './rects.ts'

export type Point = { x: number; y: number }

function fmt(n: number): string {
  return Number.isFinite(n) ? String(Math.round(n * 1000) / 1000) : '0'
}

/** Sechseck-Eckpunkte in ein Rechteck eingepasst (nicht zwingend regelmäßig, wenn das Rechteck es nicht ist). */
export function hexagonPoints(r: Rect, orientation: HexOrientation = 'flat'): Point[] {
  const { x, y, w, h } = r
  if (orientation === 'pointy') {
    return [
      { x: x + w / 2, y },
      { x: x + w, y: y + h / 4 },
      { x: x + w, y: y + (3 * h) / 4 },
      { x: x + w / 2, y: y + h },
      { x, y: y + (3 * h) / 4 },
      { x, y: y + h / 4 },
    ]
  }
  return [
    { x: x + w / 4, y },
    { x: x + (3 * w) / 4, y },
    { x: x + w, y: y + h / 2 },
    { x: x + (3 * w) / 4, y: y + h },
    { x: x + w / 4, y: y + h },
    { x, y: y + h / 2 },
  ]
}

/**
 * Konvexes Polygon um `d` nach innen versetzen (jede Kante parallel verschieben, benachbarte
 * Kanten schneiden). Für den Sicherheitsbereich eines Sechsecks; ein reines Skalieren wäre
 * bei nicht-regelmäßigen Sechsecken ungleichmäßig.
 */
export function offsetConvexPolygon(points: Point[], d: number): Point[] {
  const n = points.length
  if (n < 3 || !(d > 0)) return points.map((p) => ({ ...p }))
  // Orientierung bestimmen (Vorzeichen der Fläche), damit "innen" stimmt.
  let area = 0
  for (let i = 0; i < n; i++) {
    const a = points[i]!
    const b = points[(i + 1) % n]!
    area += a.x * b.y - b.x * a.y
  }
  const sign = area >= 0 ? 1 : -1

  const lines = points.map((a, i) => {
    const b = points[(i + 1) % n]!
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len = Math.hypot(dx, dy) || 1
    // Innen-Normale
    const nx = (sign * -dy) / len
    const ny = (sign * dx) / len
    return { p: { x: a.x + nx * d, y: a.y + ny * d }, dir: { x: dx, y: dy } }
  })

  const out: Point[] = []
  for (let i = 0; i < n; i++) {
    const l1 = lines[(i - 1 + n) % n]!
    const l2 = lines[i]!
    const det = l1.dir.x * l2.dir.y - l1.dir.y * l2.dir.x
    if (Math.abs(det) < 1e-9) {
      out.push({ ...l2.p })
      continue
    }
    const t = ((l2.p.x - l1.p.x) * l2.dir.y - (l2.p.y - l1.p.y) * l2.dir.x) / det
    out.push({ x: l1.p.x + l1.dir.x * t, y: l1.p.y + l1.dir.y * t })
  }
  return out
}

function polygonPath(points: Point[]): string {
  if (!points.length) return ''
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${fmt(p.x)} ${fmt(p.y)}`).join(' ') + ' Z'
}

function roundedRectPath(r: Rect, radius: number): string {
  const rad = Math.max(0, Math.min(radius, r.w / 2, r.h / 2))
  if (rad <= 0) return rectPath(r)
  const { x, y, w, h } = r
  return [
    `M${fmt(x + rad)} ${fmt(y)}`,
    `H${fmt(x + w - rad)}`,
    `A${fmt(rad)} ${fmt(rad)} 0 0 1 ${fmt(x + w)} ${fmt(y + rad)}`,
    `V${fmt(y + h - rad)}`,
    `A${fmt(rad)} ${fmt(rad)} 0 0 1 ${fmt(x + w - rad)} ${fmt(y + h)}`,
    `H${fmt(x + rad)}`,
    `A${fmt(rad)} ${fmt(rad)} 0 0 1 ${fmt(x)} ${fmt(y + h - rad)}`,
    `V${fmt(y + rad)}`,
    `A${fmt(rad)} ${fmt(rad)} 0 0 1 ${fmt(x + rad)} ${fmt(y)}`,
    'Z',
  ].join(' ')
}

function rectPath(r: Rect): string {
  return `M${fmt(r.x)} ${fmt(r.y)} H${fmt(r.x + r.w)} V${fmt(r.y + r.h)} H${fmt(r.x)} Z`
}

function ellipsePath(r: Rect): string {
  const rx = r.w / 2
  const ry = r.h / 2
  const cx = r.x + rx
  const cy = r.y + ry
  return [
    `M${fmt(cx - rx)} ${fmt(cy)}`,
    `A${fmt(rx)} ${fmt(ry)} 0 1 0 ${fmt(cx + rx)} ${fmt(cy)}`,
    `A${fmt(rx)} ${fmt(ry)} 0 1 0 ${fmt(cx - rx)} ${fmt(cy)}`,
    'Z',
  ].join(' ')
}

export type ShapeSpec = {
  shape: ShapeKind
  cornerRadius?: number
  orientation?: HexOrientation
}

/**
 * SVG-Pfad (`d`) der Form, in das Rechteck `r` eingepasst. Einheit = Einheit von `r`.
 * `inset` versetzt die Form um diesen Betrag nach innen (Sicherheitsbereich).
 * Einsetzbar in `<path d="…">` und in Canvas via `new Path2D(d)`.
 * `custom` fällt auf ein Rechteck zurück; die echte Maske kommt aus der SVG-Datei.
 */
export function shapePath(spec: ShapeSpec, r: Rect, inset = 0): string {
  const shape = spec.shape
  if (shape === 'circle') return ellipsePath(inset > 0 ? insetRect(r, inset) : r)
  if (shape === 'hexagon') {
    const pts = hexagonPoints(r, spec.orientation ?? 'flat')
    return polygonPath(inset > 0 ? offsetConvexPolygon(pts, inset) : pts)
  }
  if (shape === 'rounded') {
    const rr = inset > 0 ? insetRect(r, inset) : r
    return roundedRectPath(rr, Math.max(0, (spec.cornerRadius ?? 0) - inset))
  }
  return rectPath(inset > 0 ? insetRect(r, inset) : r)
}

/** Formspezifikation direkt aus einer Produktgeometrie. */
export function shapeSpecFromGeometry(g: ProductGeometry): ShapeSpec {
  return {
    shape: g.shape,
    cornerRadius: g.cornerRadiusMm,
    orientation: g.shapeParams?.orientation === 'pointy' ? 'pointy' : 'flat',
  }
}

/**
 * Schnittkante, Sicherheitsbereich und Beschnitt-Box in Pixeln für einen Crop-Rahmen, der das
 * volle Format (Endformat + Beschnitt) darstellt. Für Canvas-Overlays (CanvasCropperLite) und
 * SVG-Overlays (GuidesOverlay) gleichermaßen.
 *
 * @param target  Crop-Rahmen in px
 * @param swapped Hoch-/Querformat gegenüber der Geometrie getauscht
 */
export function guidePathsForFrame(g: ProductGeometry, target: Rect, swapped = false): {
  bleedPx: number
  safePx: number
  cutPath: string
  safePath: string
  bleedPath: string
  trimRect: Rect
} {
  const fullW = (swapped ? g.trimHeightMm : g.trimWidthMm) + 2 * g.bleedMm
  const fullH = (swapped ? g.trimWidthMm : g.trimHeightMm) + 2 * g.bleedMm
  const scale = fullW > 0 && fullH > 0 ? Math.min(target.w / fullW, target.h / fullH) : 0
  const bleedPx = scale > 0 ? g.bleedMm * scale : 0
  const safePx = scale > 0 ? g.safeMarginMm * scale : 0
  const trimRect = insetRect(target, bleedPx)
  const spec = shapeSpecFromGeometry(g)
  const specPx: ShapeSpec = { ...spec, cornerRadius: (spec.cornerRadius ?? 0) * (scale || 0) }
  return {
    bleedPx,
    safePx,
    cutPath: shapePath(specPx, trimRect, 0),
    safePath: safePx > 0 ? shapePath(specPx, trimRect, safePx) : '',
    bleedPath: rectPath(target),
    trimRect,
  }
}

/** CSS `clip-path` für die Produktansicht (Form auf die Trim-Box angewendet). */
export function shapeClipPathCss(spec: ShapeSpec, r: Rect): string {
  if (spec.shape === 'circle') return `ellipse(${fmt(r.w / 2)}px ${fmt(r.h / 2)}px at ${fmt(r.x + r.w / 2)}px ${fmt(r.y + r.h / 2)}px)`
  if (spec.shape === 'hexagon') {
    const pts = hexagonPoints(r, spec.orientation ?? 'flat')
    return `polygon(${pts.map((p) => `${fmt(p.x)}px ${fmt(p.y)}px`).join(', ')})`
  }
  if (spec.shape === 'rounded') return `inset(${fmt(r.y)}px ${fmt(r.x)}px ${fmt(r.y)}px ${fmt(r.x)}px round ${fmt(spec.cornerRadius ?? 0)}px)`
  return `inset(${fmt(r.y)}px ${fmt(r.x)}px ${fmt(r.y)}px ${fmt(r.x)}px)`
}
